import { Component, Inject, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { FormControl } from '@angular/forms';
import { ModalController, ToastController } from '@ionic/angular'; // Import ToastController
import { ApplicationFacade, OpenTripPlannerFacade, Route, RouteGroups, SearchUtil, Stop } from '@ridango/components';
import { DisturbanceSubscription, NotificationSettingsService, ShapeType, NotificationTabType } from '@ridango/core';
import { BehaviorSubject, combineLatest, merge, of } from 'rxjs';
import { debounceTime, map, shareReplay, filter, mapTo, switchMap, take } from 'rxjs/operators';
import { SubscriptionsConfirmModalComponent } from './subscriptions-confirm-modal/subscriptions-confirm-modal.component';
import Env from '@ridango/core/env';
import { TranslateService } from '@ngx-translate/core';

type Segment = 'routes' | 'stops' | 'routeGroups' | 'search';
@Component({
    selector: 'rwidget-add-subscriptions',
    templateUrl: './add-subscriptions.component.html',
    styleUrls: ['./add-subscriptions.component.scss']
})
export class AddSubscriptionsComponent implements OnInit {
    searchControl = new FormControl('');
    segment: Segment = 'routeGroups';
    allRoutes$ = this.otpFacade.routes$;
    selectedGroup$ = new BehaviorSubject<RouteGroups>(null);
    routes$ = combineLatest([this.allRoutes$, this.selectedGroup$]).pipe(
        map(([routes, group]) => {
            if (!group) return [];
            const routeMap = new Map<string, Route>();
            routes.forEach((r) => routeMap.set(r.id, r));
            return group.routes.map((r) => routeMap.get(r.gtfsId));
        })
    );
    stops$ = this.otpFacade.stops$;
    routesGroups$ = this.otpFacade.routesGroups$;
    subscriptions$ = new BehaviorSubject<DisturbanceSubscription[]>([]);
    stopSubscriptions$ = this.subscriptions$.pipe(map((subs) => new Set(subs.map((sub) => sub.stopId))));
    routeSubscriptions$ = this.subscriptions$.pipe(map((subs) => new Set(subs.map((sub) => sub.routeId))));
    ShapeType = ShapeType;

    private displayedStopsCount$ = new BehaviorSubject<number>(
        this.env?.DISRUPTION_SUBSCRIPTIONS?.SEARCH_INITIAL_LIMIT ?? 10
    );
    private displayedRoutesCount$ = new BehaviorSubject<number>(
        this.env?.DISRUPTION_SUBSCRIPTIONS?.SEARCH_INITIAL_LIMIT ?? 10
    );
    private allSearchStops$ = new BehaviorSubject<Stop[]>([]);
    private allSearchRoutes$ = new BehaviorSubject<Route[]>([]);

    hasValidSearchTerm$ = this.searchControl.valueChanges.pipe(
        map((value) => value && /[a-zA-Z0-9äöüõžšÄÖÜÕŽŠ]/.test(value)),
        shareReplay(1)
    );

    private isNumberSearch$ = this.searchControl.valueChanges.pipe(
        map((term) => SearchUtil.startsWithNumber(term || '', this.env)),
        shareReplay(1)
    );

    searchStops$ = combineLatest([
        this.searchControl.valueChanges.pipe(
            debounceTime(500),
            map((term) => term?.toLowerCase() || '')
        ),
        this.hasValidSearchTerm$.pipe(debounceTime(500)),
        this.isNumberSearch$,
        this.displayedStopsCount$
    ]).pipe(
        switchMap(([term, isValid, isNumberSearch, displayCount]) => {
            if (!isValid || isNumberSearch) {
                this.allSearchStops$.next([]);
                return of([]);
            }
            return this.stops$.pipe(
                map((stops) => {
                    let filtered = stops.filter((s) => s.name.toLowerCase().includes(term));

                    filtered = SearchUtil.sortByRelevance(filtered, term, 'name', false);

                    this.allSearchStops$.next(filtered);
                    return filtered.slice(0, displayCount);
                })
            );
        }),
        shareReplay(1)
    );

    searchRoutes$ = combineLatest([
        this.searchControl.valueChanges.pipe(
            debounceTime(500),
            map((term) => term?.toLowerCase() || '')
        ),
        this.hasValidSearchTerm$.pipe(debounceTime(500)),
        this.isNumberSearch$,
        this.displayedRoutesCount$
    ]).pipe(
        switchMap(([term, isValid, isNumberSearch, displayCount]) => {
            if (!isValid || !isNumberSearch) {
                this.allSearchRoutes$.next([]);
                return of([]);
            }
            return this.allRoutes$.pipe(
                map((routes) => {
                    let filtered = routes.filter((r) => r.shortName.toLowerCase().includes(term));

                    filtered = SearchUtil.sortByRelevance(filtered, term, 'shortName', true);

                    this.allSearchRoutes$.next(filtered);
                    return filtered.slice(0, displayCount);
                })
            );
        }),
        shareReplay(1)
    );

    isSearching$ = merge(
        this.hasValidSearchTerm$.pipe(
            filter((isValid) => isValid),
            mapTo(true)
        ),
        combineLatest([this.searchStops$, this.searchRoutes$]).pipe(mapTo(false))
    ).pipe(shareReplay(1));

    noResults$ = combineLatest([
        this.hasValidSearchTerm$,
        this.isSearching$,
        this.searchStops$,
        this.searchRoutes$
    ]).pipe(
        map(([valid, loading, stops, routes]) => valid && !loading && stops.length === 0 && routes.length === 0),
        shareReplay(1)
    );

    hasMoreResults$ = combineLatest([
        this.allSearchStops$,
        this.allSearchRoutes$,
        this.searchStops$,
        this.searchRoutes$
    ]).pipe(
        map(([allStops, allRoutes, displayedStops, displayedRoutes]) => {
            return allStops.length > displayedStops.length || allRoutes.length > displayedRoutes.length;
        }),
        shareReplay(1)
    );

    selectedRoutes: Route[] = [];
    selectedStops: Stop[] = [];
    selectionLimit = this.env?.DISRUPTION_SUBSCRIPTIONS?.SELECTION_LIMIT ?? 5;
    assetsPath$ = this.applicationFacade.assetsPath$;

    NotificationTabType = NotificationTabType;
    enabledTabs: NotificationTabType[] = this.env?.DISRUPTION_SUBSCRIPTIONS?.ENABLED_TABS ?? [
        NotificationTabType.ROUTE_GROUPS,
        NotificationTabType.ROUTES,
        NotificationTabType.STOPS
    ];
    showStopsTab: boolean;

    constructor(
        @Inject('ENV') public env: Env,
        private location: Location,
        private otpFacade: OpenTripPlannerFacade,
        private notificationService: NotificationSettingsService,
        private modalCtrl: ModalController,
        private toastCtrl: ToastController,
        private translate: TranslateService,
        private applicationFacade: ApplicationFacade
    ) {}

    ngOnInit(): void {
        this.otpFacade.getRoutesGroups();
        this.otpFacade.getAllStops();
        this.otpFacade.getAllRoutes();
        this.refreshSubscriptions();
        this.showStopsTab = this.isTabEnabled(NotificationTabType.STOPS);
    }

    isRouteSelected(route: Route): boolean {
        return this.selectedRoutes.some((r) => r.id === route.id);
    }

    isStopSelected(stop: Stop): boolean {
        return this.selectedStops.some((s) => s.id === stop.id);
    }

    handleBackClicked() {
        this.location.back();
    }
    tabChange(event: { detail: { value: Segment } }) {
        this.segment = event.detail.value;
        this.selectedRoutes = [];
        if (this.isTabEnabled(NotificationTabType.STOPS)) {
            this.selectedStops = [];
        }
    }

    showRoutes(group: RouteGroups) {
        this.selectedGroup$.next(group);
        this.segment = 'routes';
    }

    showSearch() {
        this.segment = 'search';
        this.selectedRoutes = [];
        if (this.isTabEnabled(NotificationTabType.STOPS)) {
            this.selectedStops = [];
        }
    }

    refreshSubscriptions() {
        this.notificationService
            .getDisturbanceSubscriptions()
            .pipe(take(1))
            .subscribe((subs) => this.subscriptions$.next(subs));
    }

    isTabEnabled(tab: NotificationTabType): boolean {
        return this.enabledTabs.includes(tab);
    }

    loadMoreSearchResults() {
        const loadMoreAmount = this.env?.DISRUPTION_SUBSCRIPTIONS?.SEARCH_LOAD_MORE_LIMIT ?? 10;
        const currentStopsCount = this.displayedStopsCount$.getValue();
        const currentRoutesCount = this.displayedRoutesCount$.getValue();

        this.displayedStopsCount$.next(currentStopsCount + loadMoreAmount);
        this.displayedRoutesCount$.next(currentRoutesCount + loadMoreAmount);
    }

    async toggleRouteSubscription(event: { detail: { checked: boolean }; target: any }, selectedRoute: Route) {
        const isChecked = event.detail.checked;

        if (isChecked) {
            if (!this.isRouteSelected(selectedRoute)) {
                this.selectedRoutes.push(selectedRoute);
            }
            if (
                this.segment === 'search' &&
                this.selectedRoutes.length + this.selectedStops.length >= this.selectionLimit
            ) {
                await this.presentToast(this.translate.instant('SELECTED_MAXIMUM_ITEMS'));
                return;
            } else if (this.segment === 'routes' && this.selectedRoutes.length >= this.selectionLimit) {
                await this.presentToast(this.translate.instant('SELECTED_MAXIMUM_ITEMS'));
                return;
            }
        } else {
            this.selectedRoutes = this.selectedRoutes.filter((route) => route.id !== selectedRoute.id);
        }
    }

    async toggleStopSubscription(event: { detail: { checked: boolean }; target: any }, selectedStop: Stop) {
        const isChecked = event.detail.checked;

        if (isChecked) {
            if (!this.isStopSelected(selectedStop)) {
                this.selectedStops.push(selectedStop);
            }
            if (
                this.segment === 'search' &&
                this.selectedRoutes.length + this.selectedStops.length >= this.selectionLimit
            ) {
                await this.presentToast(this.translate.instant('SELECTED_MAXIMUM_ITEMS'));
                return;
            } else if (this.segment === 'stops' && this.selectedStops.length >= this.selectionLimit) {
                await this.presentToast(this.translate.instant('SELECTED_MAXIMUM_ITEMS'));
                return;
            }
        } else {
            this.selectedStops = this.selectedStops.filter((stop) => stop.id !== selectedStop.id);
        }
    }

    getAllSelectedRouteStops() {
        this.openModal();
    }

    async openModal() {
        const modal = await this.modalCtrl.create({
            component: SubscriptionsConfirmModalComponent,
            cssClass: 'subscriptions-confirm-modal',
            componentProps: {
                selectedNotification: this.segment,
                stopsRoutesData: { stops: this.selectedStops, routes: this.selectedRoutes }
            }
        });
        await modal.present();
        const { role } = await modal.onWillDismiss();
        if (role === 'confirm') {
            this.refreshSubscriptions();
        }
    }

    async presentToast(message: string) {
        const toast = await this.toastCtrl.create({
            message: 'ⓘ ' + message,
            duration: 5000,
            position: 'top',
            cssClass: 'custom-toast',
            buttons: [{ icon: 'close-circle', role: 'cancel' }]
        });
        await toast.present();
    }
}
